import { Card, Stack, Typography } from "@altimateai/lego";
import { formatNumber } from "@/uiCore";
import { useAppState } from "@/modules/app";

interface Props {
  tasksEnabled: number;
  tasksTotal: number;
  // Windowed totals of the per-day auto-tune savings series (the same
  // data feeding the Cost Savings chart), scoped to the page date range.
  // Each tile renders only when its value is a positive number;
  // otherwise it is omitted from the row (no empty card).
  potentialSavingsUsd?: number | null;
  actualSavingsUsd?: number | null;
  atScopedSpendUsd?: number | null;
  // Label for the first tile's ``enabled/total`` count. Defaults to
  // "Tasks" (jobs). Clusters pass "Recommendations" since the count is
  // over per-cluster recommendations, not job tasks.
  tasksLabel?: string;
}

const renderTile = (testId: string, label: string, value: string) => (
  <Card key={testId} className="al-p-4" data-testid={testId}>
    <Stack direction="column" gap="xxs">
      <Typography size="sm" className="al-text-stroke-dark">
        {label}
      </Typography>
      <Typography variant="h3" weight="semibold" className="al-text-dark">
        {value}
      </Typography>
    </Stack>
  </Card>
);

// Top-of-panel overview row. Each tile renders independently — tiles
// with no underlying value are omitted so the row collapses gracefully.
const AutoTuneOverviewCards = ({
  tasksEnabled,
  tasksTotal,
  potentialSavingsUsd,
  actualSavingsUsd,
  atScopedSpendUsd,
  tasksLabel = "Tasks",
}: Props) => {
  const { currency } = useAppState();

  const showSpend = atScopedSpendUsd != null;

  // Money tiles always show two decimals (e.g. "$30.50", not "$30.5").
  // ``minimumFractionDigits: 2`` pads the cents and ``trailingZeroDisplay:
  // "auto"`` overrides the formatter's default integer-stripping so whole
  // dollars still render their cents. Cent precision also keeps small
  // daily savings (e.g. $0.42) from rounding down to a misleading "$0".
  const formatMoney = (value: number) =>
    formatNumber(value, {
      currency,
      minimumFractionDigits: 2,
      trailingZeroDisplay: "auto",
    } as Intl.NumberFormatOptions);
  const savingsSummary = (value?: number | null) =>
    value != null && value > 0 ? formatMoney(value) : null;
  const potentialSummary = savingsSummary(potentialSavingsUsd);
  const actualSummary = savingsSummary(actualSavingsUsd);

  return (
    <div
      className="al-grid al-grid-cols-2 lg:al-grid-cols-4 al-gap-4"
      aria-label="Auto Tune overview"
    >
      {renderTile("auto-tune-overview-tasks", tasksLabel, `${tasksEnabled}/${tasksTotal}`)}
      {potentialSummary &&
        renderTile("auto-tune-overview-potential-savings", "Potential Savings", potentialSummary)}
      {actualSummary &&
        renderTile("auto-tune-overview-actual-savings", "Actual Savings", actualSummary)}
      {showSpend && renderTile("auto-tune-overview-spend", "Spend", formatMoney(atScopedSpendUsd))}
    </div>
  );
};

export default AutoTuneOverviewCards;
